import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { logger } from "../logging/logger";
import { parseSubjects, type ParseSubjectsResult } from "./parseSubjects";

interface SubjectInputProps {
    onParsed: (result: ParseSubjectsResult) => void;
    /** Optional initial value (e.g., restored from sessionStorage). */
    initialText?: string;
    /** Notifies the parent on every text change so it can persist the draft. */
    onTextChange?: (text: string) => void;
}

const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 4 MiB; >100k UUIDs even with newlines.

/**
 * Subject-ID input. Accepts a `.csv` / `.txt` file upload OR a paste into
 * the textarea. Always parses with `parseSubjects` and reports the result
 * via `onParsed` so the parent can display the validation summary and
 * attach the validated UUID list to the submission.
 *
 * Owns its own text state but exposes it via `onTextChange` so the parent
 * can persist drafts across navigation (PRD-001 user scenario: CSMs paste
 * thousands of UUIDs and don't want to lose them when switching tabs).
 */
export function SubjectInput({ onParsed, initialText = "", onTextChange }: SubjectInputProps) {
    const [text, setText] = useState(initialText);
    const [fileName, setFileName] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);

    const result = useMemo(() => parseSubjects(text), [text]);

    useEffect(() => {
        onParsed(result);
    }, [result, onParsed]);

    useEffect(() => {
        onTextChange?.(text);
    }, [text, onTextChange]);

    const handleFile = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file
        if (!file) return;
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setFileError(`File too large (${file.size} bytes; max ${MAX_FILE_SIZE_BYTES}).`);
            return;
        }
        setFileError(null);
        try {
            const contents = await file.text();
            setFileName(file.name);
            setText(contents);
        } catch (err) {
            logger.error("subject-input: file read failed", err);
            setFileError("Could not read file.");
        }
    }, []);

    const clear = () => {
        setText("");
        setFileName(null);
        setFileError(null);
    };

    return (
        <div className="subject-input">
            <div className="subject-input-controls">
                <label className="btn btn-secondary btn-sm file-upload">
                    <i className="fa-solid fa-file-arrow-up" aria-hidden="true" />
                    <span>Upload .csv or .txt</span>
                    <input
                        type="file"
                        accept=".csv,.txt,text/csv,text/plain"
                        onChange={handleFile}
                        data-testid="subject-input-file"
                        style={{ display: "none" }}
                    />
                </label>
                {fileName ? (
                    <span
                        className="file-name td-id"
                        data-testid="subject-input-filename"
                    >
                        <i className="fa-solid fa-file-lines" aria-hidden="true" /> {fileName}
                    </span>
                ) : null}
                {(text || fileName) && (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={clear}
                        data-testid="subject-input-clear"
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" /> Clear
                    </button>
                )}
            </div>
            {fileError && (
                <div
                    className="callout callout-error"
                    data-testid="subject-input-file-error"
                >
                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    <span>{fileError}</span>
                </div>
            )}
            <textarea
                className="f-textarea"
                aria-label="Subject IDs"
                rows={6}
                placeholder="Paste subject UUIDs (one per line or comma-separated)…"
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    setFileName(null);
                }}
                data-testid="subject-input-textarea"
            />
            {(result.totalRows > 0 || text.trim().length > 0) && (
                <ParseSummary result={result} />
            )}
        </div>
    );
}

function ParseSummary({ result }: { result: ParseSubjectsResult }) {
    return (
        <div className="parse-summary" data-testid="subject-input-summary">
            <p>
                <strong data-testid="parse-valid-count">{result.valid.length}</strong>
                {" unique subject ID"}
                {result.valid.length === 1 ? "" : "s"} loaded
                {result.duplicateCount > 0 && (
                    <>
                        {", "}
                        <span data-testid="parse-duplicate-count">
                            {result.duplicateCount}
                        </span>
                        {" duplicate"}
                        {result.duplicateCount === 1 ? "" : "s"}
                        {" removed"}
                    </>
                )}
                {"."}
            </p>
            {result.invalid.length > 0 && (
                <details data-testid="parse-invalid-section" open>
                    <summary>
                        <span data-testid="parse-invalid-count">
                            {result.invalid.length}
                        </span>
                        {" row"}
                        {result.invalid.length === 1 ? "" : "s"}
                        {" failed UUID validation"}
                    </summary>
                    <ul>
                        {result.invalid.slice(0, 50).map((row) => (
                            <li key={`${row.row}:${row.value}`}>
                                <span>row {row.row}: </span>
                                <code>{row.value}</code>
                            </li>
                        ))}
                        {result.invalid.length > 50 && (
                            <li>…and {result.invalid.length - 50} more</li>
                        )}
                    </ul>
                </details>
            )}
        </div>
    );
}
